import React, { useState, useEffect } from 'react';
import axios from 'axios';

const TemplateBasedMarksheetEntry = ({ student, onClose, onSave }) => {
  const [marksheet, setMarksheet] = useState(null);
  const [template, setTemplate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (student) {
      fetchMarksheetAndTemplate();
    }
  }, [student]);

  const fetchMarksheetAndTemplate = async () => {
    try {
      setLoading(true);
      
      // Fetch marksheet template for the class
      const templateResponse = await axios.get(`/api/marksheet-template/${student.class}`);
      setTemplate(templateResponse.data);
      
      // Fetch existing marksheet or create default structure
      try {
        const marksheetResponse = await axios.get(`/api/marksheet/${student._id}`);
        setMarksheet(marksheetResponse.data);
      } catch (error) {
        // Create default marksheet structure based on template
        const defaultMarksheet = {
          studentId: student._id,
          name: student.name,
          fathersName: student.fathersName || '',
          class: student.class,
          section: student.section || '',
          visibility: {
            term1: false,
            term2: false,
            halfYearly: false,
            term3: false,
            project: false,
            annualExam: false,
            finalResult: false
          },
          exams: {}
        };
        
        // Initialize exam structure based on template
        if (templateResponse.data?.examStructure) {
          Object.keys(templateResponse.data.examStructure).forEach(examType => {
            if (templateResponse.data.examStructure[examType].enabled) {
              defaultMarksheet.exams[examType] = {};
              // Initialize subjects
              templateResponse.data.subjects.forEach(subject => {
                defaultMarksheet.exams[examType][subject.name] = '';
              });
              // Initialize additional fields
              if (templateResponse.data.additionalFields) {
                templateResponse.data.additionalFields.forEach(field => {
                  defaultMarksheet.exams[examType][field.name] = '';
                });
              }
            }
          });
        }
        
        setMarksheet(defaultMarksheet);
      }
    } catch (error) {
      console.error('Error fetching template:', error);
      alert('No marksheet template found for this class. Please contact Admin Level 1 to set up the marksheet pattern.');
      onClose();
    } finally {
      setLoading(false);
    }
  };

  const updateMarks = (examType, subject, value) => {
    // Check if this is an additional field (attendance/co-curricular)
    const isAdditionalField = template?.additionalFields?.some(field => field.name === subject);
    
    if (!isAdditionalField) {
      const examConfig = template?.examStructure?.[examType];
      let maxMarks;
      
      // Check if subject-specific marks are enabled for this exam
      if (examConfig?.useSubjectSpecificMarks && examConfig?.subjectMaxMarks?.[subject]) {
        maxMarks = examConfig.subjectMaxMarks[subject];
      } else {
        maxMarks = examConfig?.maxMarks || 100;
      }
      
      // Allow empty string or valid numbers (including 0)
      if (value !== '' && value !== null && value !== undefined) {
        const numValue = parseInt(value);
        // Check if it's a valid number and within range
        if (isNaN(numValue) || numValue < 0 || numValue > maxMarks) {
          const subjectDisplayName = template.subjects.find(s => s.name === subject)?.displayName || subject;
          if (isNaN(numValue) || numValue < 0) {
            alert(`Please enter a valid number (0 or above) for ${subjectDisplayName}`);
          } else {
            alert(`Maximum marks for ${subjectDisplayName} in ${examConfig?.displayName || examType} is ${maxMarks}`);
          }
          return;
        }
      }
    }
    
    console.log(`Updating marks: ${examType}.${subject} = ${value}`);
    
    setMarksheet(prev => {
      const updated = {
        ...prev,
        exams: {
          ...prev.exams,
          [examType]: {
            ...prev.exams[examType],
            // For additional fields, keep as string; for marks, convert to number or keep empty
            [subject]: isAdditionalField ? value : (value === '' ? '' : parseInt(value))
          }
        }
      };
      console.log('Updated marksheet:', updated);
      return updated;
    });
  };

  const toggleVisibility = (examType) => {
    setMarksheet(prev => ({
      ...prev,
      visibility: {
        ...prev.visibility,
        [examType]: !prev.visibility[examType]
      }
    }));
  };

  const saveMarksheet = async () => {
    try {
      setSaving(true);
      console.log('Saving marksheet:', marksheet);
      
      const response = await axios.put(`/api/marksheet/${student._id}`, marksheet);
      console.log('Marksheet saved successfully:', response.data);
      
      alert('Marksheet saved successfully!');
      onSave && onSave();
      onClose();
    } catch (error) {
      console.error('Error saving marksheet:', error);
      alert(`Error saving marksheet: ${error.response?.data?.message || error.message}`);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', background: 'rgba(0,0,0,0.8)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 5000 }}>
        <div style={{ background: 'white', padding: '40px', borderRadius: '15px', textAlign: 'center' }}>
          <div style={{ fontSize: '48px', marginBottom: '20px' }}>📊</div>
          <div style={{ fontSize: '18px', fontWeight: 'bold' }}>Loading marksheet template...</div>
        </div>
      </div>
    );
  }

  if (!template) {
    return (
      <div style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', background: 'rgba(0,0,0,0.8)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 5000 }}>
        <div style={{ background: 'white', padding: '40px', borderRadius: '15px', textAlign: 'center', maxWidth: '500px' }}>
          <div style={{ fontSize: '48px', marginBottom: '20px' }}>⚠️</div>
          <div style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '15px' }}>No Marksheet Template Found</div>
          <div style={{ fontSize: '14px', color: '#666', marginBottom: '20px' }}>
            Admin Level 1 needs to create a marksheet pattern for class {student.class} before marks can be entered.
          </div>
          <button onClick={onClose} style={{ background: '#dc2626', color: 'white', border: 'none', padding: '10px 20px', borderRadius: '8px', cursor: 'pointer' }}>
            Close
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', background: 'rgba(0,0,0,0.8)', display: 'flex', justifyContent: 'center', alignItems: 'flex-start', zIndex: 5000, overflow: 'auto', padding: '20px' }}>
      <div style={{ background: 'white', borderRadius: '15px', width: '100%', maxWidth: '1200px', maxHeight: '90vh', overflow: 'auto', boxShadow: '0 20px 40px rgba(0,0,0,0.3)' }}>
        
        {/* Header */}
        <div style={{ padding: '20px', borderBottom: '2px solid #3b82f6', background: 'linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <h2 style={{ margin: 0, color: '#1e40af', fontSize: '24px', fontWeight: 'bold' }}>📊 Enter Marks - {student.name}</h2>
              <p style={{ margin: '5px 0 0 0', color: '#1e40af' }}>Class {student.class} | Pattern defined by Admin Level 1</p>
            </div>
            <button onClick={onClose} style={{ background: '#dc2626', color: 'white', border: 'none', padding: '10px 15px', borderRadius: '50%', cursor: 'pointer', fontSize: '18px' }}>✕</button>
          </div>
        </div>

        <div style={{ padding: '20px' }}>
          {/* Student Info */}
          <div style={{ marginBottom: '20px', padding: '15px', background: '#f8fafc', borderRadius: '10px', border: '1px solid #e2e8f0' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
              <div style={{ width: '60px', height: '60px', borderRadius: '50%', background: student.profilePicture ? `url(${student.profilePicture}) center/cover` : '#e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '24px' }}>
                {!student.profilePicture && '👤'}
              </div>
              <div>
                <h3 style={{ margin: 0, fontSize: '20px' }}>{student.name}</h3>
                <p style={{ margin: '2px 0', color: '#666' }}>Father: {student.fathersName}</p>
                <p style={{ margin: '2px 0', color: '#666' }}>Class: {student.class}{student.section && ` - Section ${student.section}`}</p>
              </div>
            </div>
          </div>

          {/* Enhanced Template Info */}
          <div style={{ marginBottom: '20px', padding: '15px', background: 'linear-gradient(135deg, #fff7ed 0%, #fed7aa 100%)', borderRadius: '10px', border: '2px solid #f97316' }}>
            <h4 style={{ margin: '0 0 10px 0', color: '#ea580c' }}>📋 Marksheet Pattern (Admin Level 1 Controlled)</h4>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '10px' }}>
              <div>
                <strong style={{ color: '#ea580c' }}>Subjects:</strong>
                <div style={{ fontSize: '14px', color: '#ea580c' }}>
                  {template.subjects.map(subject => subject.displayName).join(', ')}
                </div>
              </div>
              <div>
                <strong style={{ color: '#ea580c' }}>Enabled Exams:</strong>
                <div style={{ fontSize: '14px', color: '#ea580c' }}>
                  {Object.entries(template.examStructure)
                    .filter(([_, config]) => config.enabled)
                    .map(([exam, config]) => {
                      const examName = config.displayName || exam.replace(/([A-Z])/g, ' $1');
                      const marksInfo = config.useSubjectSpecificMarks ? '(Subject-specific marks)' : `(${config.maxMarks} marks)`;
                      return `${examName} ${marksInfo}`;
                    })
                    .join(', ')}
                </div>
              </div>
            </div>
            {Object.values(template.examStructure).some(config => config.useSubjectSpecificMarks) && (
              <div style={{ marginTop: '10px', padding: '8px', background: 'rgba(255,255,255,0.7)', borderRadius: '6px' }}>
                <div style={{ fontSize: '12px', color: '#ea580c', fontWeight: 'bold' }}>💡 Subject-Specific Marks Enabled:</div>
                <div style={{ fontSize: '11px', color: '#ea580c', marginTop: '4px' }}>
                  Different subjects may have different maximum marks for some exams. Check individual subject labels for specific limits.
                </div>
              </div>
            )}
          </div>

          {/* Marks Entry */}
          {Object.entries(template.examStructure)
            .filter(([_, config]) => config.enabled)
            .sort(([,a], [,b]) => (a.order || 0) - (b.order || 0))
            .map(([examType, examConfig]) => (
              <div key={examType} style={{ marginBottom: '25px', padding: '20px', background: '#f8fafc', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                  <h4 style={{ margin: 0, color: '#1e293b' }}>
                    {examConfig.displayName || examType.replace(/([A-Z])/g, ' $1')} 
                    {examConfig.useSubjectSpecificMarks ? 
                      <span style={{ fontSize: '12px', color: '#6b7280' }}>(Subject-specific max marks)</span> : 
                      <span style={{ fontSize: '12px', color: '#6b7280' }}>(Max: {examConfig.maxMarks} marks each)</span>
                    }
                  </h4>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={marksheet.visibility[examType] || false}
                      onChange={() => toggleVisibility(examType)}
                    />
                    <span style={{ fontSize: '14px', fontWeight: 'bold', color: marksheet.visibility[examType] ? '#059669' : '#6b7280' }}>
                      {marksheet.visibility[examType] ? '👁️ Visible to Student' : '🚫 Hidden from Student'}
                    </span>
                  </label>
                </div>
                
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px' }}>
                  {template.subjects.map(subject => {
                    // Get subject-specific max marks or default
                    const subjectMaxMarks = examConfig.useSubjectSpecificMarks && examConfig.subjectMaxMarks?.[subject.name] 
                      ? examConfig.subjectMaxMarks[subject.name] 
                      : examConfig.maxMarks;
                    
                    return (
                      <div key={subject.name} style={{ padding: '15px', background: 'white', borderRadius: '8px', border: '1px solid #d1d5db' }}>
                        <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: '#374151' }}>
                          {subject.displayName}
                          <span style={{ fontSize: '12px', color: '#6b7280', fontWeight: 'normal', marginLeft: '8px' }}>
                            (Max: {subjectMaxMarks})
                          </span>
                        </label>
                        <input
                          type="number"
                          min="0"
                          max={subjectMaxMarks}
                          value={marksheet.exams[examType]?.[subject.name] ?? ''}
                          onChange={(e) => updateMarks(examType, subject.name, e.target.value)}
                          style={{ 
                            width: '100%', 
                            padding: '10px', 
                            border: '2px solid #d1d5db', 
                            borderRadius: '6px',
                            fontSize: '16px',
                            textAlign: 'center'
                          }}
                          placeholder={`0-${subjectMaxMarks}`}
                        />
                      </div>
                    );
                  })}
                </div>
                
                {/* Additional Fields */}
                {template.additionalFields && template.additionalFields.length > 0 && (
                  <div style={{ marginTop: '20px', padding: '15px', background: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                    <h5 style={{ margin: '0 0 15px 0', color: '#374151', fontSize: '16px' }}>Additional Information</h5>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px' }}>
                      {template.additionalFields.map(field => (
                        <div key={field.name} style={{ padding: '10px', background: 'white', borderRadius: '6px', border: '1px solid #d1d5db' }}>
                          <label style={{ display: 'block', marginBottom: '6px', fontWeight: 'bold', color: '#374151', fontSize: '14px' }}>
                            {field.displayName}
                          </label>
                          {field.category === 'coCurricular' ? (
                            <select
                              value={marksheet.exams[examType]?.[field.name] || ''}
                              onChange={(e) => updateMarks(examType, field.name, e.target.value)}
                              style={{ width: '100%', padding: '8px', border: '1px solid #d1d5db', borderRadius: '4px' }}
                            >
                              <option value="">Select Grade</option>
                              <option value="A+">A+</option>
                              <option value="A">A</option>
                              <option value="B+">B+</option>
                              <option value="B">B</option>
                              <option value="C+">C+</option>
                              <option value="C">C</option>
                              <option value="D">D</option>
                              <option value="F">F</option>
                            </select>
                          ) : field.category === 'attendance' ? (
                            <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                              <input
                                type="text"
                                value={typeof marksheet.exams[examType]?.[field.name] === 'string' ? marksheet.exams[examType][field.name].replace('%', '') : marksheet.exams[examType]?.[field.name] || ''}
                                onChange={(e) => {
                                  const value = e.target.value;
                                  if (value === '' || (/^\d{2}$/.test(value) && parseInt(value) <= 99)) {
                                    updateMarks(examType, field.name, value ? value + '%' : '');
                                  }
                                }}
                                style={{ 
                                  width: '100%', 
                                  padding: '8px 25px 8px 8px', 
                                  border: '1px solid #d1d5db', 
                                  borderRadius: '4px',
                                  fontSize: '14px'
                                }}
                                placeholder="Enter 2-digit (e.g., 85)"
                                maxLength="2"
                              />
                              <span style={{
                                position: 'absolute',
                                right: '8px',
                                fontSize: '14px',
                                color: '#6b7280',
                                pointerEvents: 'none'
                              }}>%</span>
                            </div>
                          ) : (
                            <input
                              type="text"
                              value={marksheet.exams[examType]?.[field.name] || ''}
                              onChange={(e) => updateMarks(examType, field.name, e.target.value)}
                              style={{ 
                                width: '100%', 
                                padding: '8px', 
                                border: '1px solid #d1d5db', 
                                borderRadius: '4px',
                                fontSize: '14px'
                              }}
                              placeholder="Enter value"
                            />
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}

          {/* Final Result Visibility */}
          <div style={{ marginBottom: '25px', padding: '15px', background: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)', borderRadius: '10px', border: '2px solid #22c55e' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={marksheet.visibility.finalResult || false}
                onChange={() => toggleVisibility('finalResult')}
              />
              <span style={{ fontSize: '16px', fontWeight: 'bold', color: '#15803d' }}>
                🏆 Show Final Result (Percentage, Grade, Division) to Student
              </span>
            </label>
          </div>

          {/* Save Button */}
          <div style={{ textAlign: 'center', paddingTop: '20px', borderTop: '2px solid #e2e8f0' }}>
            <button 
              onClick={saveMarksheet}
              disabled={saving}
              style={{ 
                background: saving ? '#9ca3af' : 'linear-gradient(135deg, #10b981 0%, #059669 100%)', 
                color: 'white', 
                border: 'none', 
                padding: '15px 40px', 
                borderRadius: '10px', 
                cursor: saving ? 'not-allowed' : 'pointer', 
                fontSize: '18px', 
                fontWeight: 'bold',
                boxShadow: '0 4px 12px rgba(16, 185, 129, 0.3)'
              }}
            >
              {saving ? '💾 Saving...' : '💾 Save Marksheet'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TemplateBasedMarksheetEntry;